import * as React from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import type { SxProps, Theme } from "@mui/material/styles";

/**
 * Syncfusion-shaped wrapper around MUI's DatePicker. Everything not listed here is forwarded to
 * DatePicker through `...otherProps`; the Syncfusion-era names (`min`/`max`/`enabled`/
 * `floatLabelType`/`change`) are translated locally.
 */
type DatePickerComponentProps = {
  sx?: SxProps<Theme>;
  value?: dayjs.ConfigType;
  min?: dayjs.ConfigType;
  max?: dayjs.ConfigType;
  /** Syncfusion date format (e.g. "dd-MMM-yy"); converted to a dayjs format below. */
  format?: string;
  /**
   * Forwarded to MUI untouched. Declared here only because this props type is closed — the
   * component already passes anything unlisted through `...otherProps`, but TypeScript rejects an
   * excess property before it gets there. Dropping "day" turns the picker into a MONTH/YEAR
   * selector, which commits as soon as a month is clicked (see the SLA export modal).
   */
  views?: readonly ("year" | "month" | "day")[];
  /** Which view opens first. Same forwarding story as {@link views}. */
  openTo?: "year" | "month" | "day";
  enabled?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Syncfusion-only; accepted and dropped. */
  floatLabelType?: string;
  id?: string;
  name?: string;
  width?: string | number;
  className?: string;
  onChange?: (args: { value: Date | null; target: { value: Date | null } }) => void;
  // BLOCKED BY A CONSUMER BUG, not by variance. This component emits `{ value: Date | null, element }`.
  // Its only consumer, `valueContentForSideBar.tsx`, routes both this callback AND
  // `NumericTextBoxComponent`'s through one shared `ValueChangeArgs = { value: number | null; … }`
  // (see its line 95, whose own comment admits it covers both) — so the shared type says the date
  // picker hands back a `number`. Because the call sites are inline arrows, `args` is contextually
  // typed from this declaration and then passed straight into that handler, so any honest payload
  // here (`value?: Date | null`) becomes a hard error at the consumer.
  //
  // Method shorthand does NOT rescue it: bivariance would accept the handler, but the failure is in
  // the consumer's body, not at the assignment — the same shape as `billingService.onFailure`.
  // The fix is to widen `ValueChangeArgs.value` to `Date | number | null` and split the two payloads,
  // which is a consumer-side change; the latent bug is that a `Date` is currently typed as a `number`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- blocked by a consumer bug, not variance; see comment above
  change?: (args: any) => void;
};

const DatePickerComponent = React.forwardRef((props: DatePickerComponentProps, ref: React.Ref<HTMLDivElement>) => {
  const internalRef = React.useRef<HTMLDivElement>(null);
  const dateRef = ref || internalRef;
  const {
    sx,
    value,
    onChange,
    change,
    format,
    min,
    max,
    enabled = true,
    disabled,
    placeholder,
    floatLabelType: _floatLabelType,
    ...otherProps
  } = props;

  // Convert syncfusion format to dayjs format
  const convertFormat = (syncFormat: string | undefined) => {
    if (!syncFormat) return "DD-MMM-YY";
    // Common syncfusion to dayjs format conversions
    return syncFormat
      .replace(/dd/g, "DD")
      .replace(/MMM/g, "MMM")
      .replace(/MM/g, "MM")
      .replace(/yy/g, "YY")
      .replace(/yyyy/g, "YYYY");
  };

  const handleChange = (newValue: dayjs.Dayjs | null) => {
    const dateValue = newValue ? newValue.toDate() : null;
    if (newValue && (!newValue.isValid?.() || !dayjs.isDayjs(newValue))) {
      return;
    }
    if (newValue && min && dayjs(newValue).startOf("day").isBefore(dayjs(min).startOf("day"))) {
      return;
    }
    if (newValue && max && dayjs(newValue).startOf("day").isAfter(dayjs(max).startOf("day"))) {
      return;
    }
    // Support both onChange and change (syncfusion uses 'change')
    if (onChange) {
      onChange({ value: dateValue, target: { value: dateValue } });
    }
    if (change) {
      change({
        value: dateValue,
        element: { ej2_instances: [{ id: props.id }] },
      });
    }
  };

  const isDisabled = disabled !== undefined ? disabled : !enabled;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        sx={{
          width: "100%",
          "&.MuiFormControl-root .MuiInputBase-root": {
            height: "2rem",
            background: "white",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--info-light-blue)",
            borderWidth: "0.0625rem",
          },
          ...sx,
        }}
        {...otherProps}
        value={value ? dayjs(value) : null}
        onChange={handleChange}
        minDate={min ? dayjs(min) : undefined}
        maxDate={max ? dayjs(max) : undefined}
        format={convertFormat(format)}
        disabled={isDisabled}
        slotProps={{
          textField: {
            placeholder: placeholder,
            size: "small",
          },
        }}
        ref={dateRef}
      />
    </LocalizationProvider>
  );
});

DatePickerComponent.displayName = "DatePickerComponent";
export default DatePickerComponent;
